export type RtkErrorClassification = "network" | "timeout" | "auth" | "server" | "client" | "unknown";

interface GraphQLErrorEntry {
	code?: null | string;
	extensions?: { code?: string; [key: string]: unknown };
	message?: string;
	[key: string]: unknown;
}

interface RtkErrorResponseShape {
	body?: unknown;
	errors?: GraphQLErrorEntry[];
	retryAfterSeconds?: number;
	status?: number;
	[key: string]: unknown;
}

export interface RtkErrorShape {
	data?: unknown;
	error?: { message?: string } | string;
	message?: string;
	name?: string;
	originalStatus?: number;
	response?: RtkErrorResponseShape;
	status?: number | string;
}

/** Single boundary cast: narrow `unknown` to the shared error shape, or
 * undefined for non-objects. All other helpers go through this. */
export function toRtkErrorShape(error: unknown): RtkErrorShape | undefined {
	if (!error || typeof error !== "object") return undefined;
	return error as RtkErrorShape;
}

/** Extracts an HTTP-style status code from any of the common error shapes. */
export function extractStatusCode(error: unknown): number | undefined {
	const err = toRtkErrorShape(error);
	if (!err) return undefined;

	if (typeof err.status === "number") return err.status;
	if (err.response && typeof err.response.status === "number") return err.response.status;
	if (typeof err.originalStatus === "number") return err.originalStatus;

	return undefined;
}

export function extractErrorMessage(error: unknown): string {
	const err = toRtkErrorShape(error);
	if (!err) return "";
	if (typeof err.message === "string") return err.message;
	if (typeof err.error === "string") return err.error;
	if (err.error && typeof err.error === "object" && typeof err.error.message === "string") return err.error.message;
	return "";
}

export function toGraphqlErrorEntries(error: unknown): GraphQLErrorEntry[] | undefined {
	if (Array.isArray(error)) {
		const entries = error.filter((entry) => !!entry && typeof entry === "object") as GraphQLErrorEntry[];
		return entries.length ? entries : undefined;
	}
	const errors = toRtkErrorShape(error)?.response?.errors;
	return errors?.length ? errors : undefined;
}

export function extractTopLevelGraphqlMessage(error: unknown): string | undefined {
	const errors = toGraphqlErrorEntries(error);
	if (!errors) return undefined;
	for (const entry of errors) {
		if (typeof entry.message === "string" && entry.message) return entry.message;
	}
	return undefined;
}

const NO_CODE_SLOT = "";

function toGraphqlCodeSlots(error: unknown): string[] {
	const errors = toGraphqlErrorEntries(error);
	if (!errors) return [];
	return errors.map((entry) => (typeof entry.extensions?.code === "string" ? entry.extensions.code : NO_CODE_SLOT));
}

const AUTH_GRAPHQL_CODES = new Set<string>([
	"AUTH_NOT_AUTHENTICATED",
	"AUTH_NOT_AUTHORIZED",
	"AUTH_NO_TENANT",
	"AUTH_READONLY_TOKEN",
	"AUTH_TENANT_MISMATCH",
	"UNAUTHENTICATED"
]);

const SERVER_GRAPHQL_CODES = new Set<string>(["INTERNAL_SERVER_ERROR", "MONGO_UNAVAILABLE"]);

const VALIDATION_GRAPHQL_CODES = new Set<string>(["AVAILABILITY_EXCEEDED", "HC0011", "HC0051", "TRIAL_LIMIT_REACHED"]);

const PAYLOAD_REFUSAL_CODES = new Set<string>(["ENTITY_DOES_NOT_EXIST", "ENTITY_NO_CHANGES"]);

const isErrorEntryObject = (slot: unknown): slot is GraphQLErrorEntry => !!slot && typeof slot === "object";

const isTopLevelErrorEntry = (slot: unknown): boolean => isErrorEntryObject(slot) && slot.extensions !== undefined;

const toPayloadCodeSlot = (slot: unknown): string =>
	isErrorEntryObject(slot) && typeof slot.code === "string" ? slot.code : NO_CODE_SLOT;

function toPayloadCodeSlots(error: unknown): string[] {
	if (!Array.isArray(error) || error.some(isTopLevelErrorEntry)) return [];
	return error.map(toPayloadCodeSlot);
}

const everySlotIsKnownPayloadRefusal = (slots: string[]): boolean =>
	slots.every((slot) => PAYLOAD_REFUSAL_CODES.has(slot));

const hasAuthCode = (slots: string[]): boolean => slots.some((slot) => AUTH_GRAPHQL_CODES.has(slot));
const hasServerCode = (slots: string[]): boolean => slots.some((slot) => SERVER_GRAPHQL_CODES.has(slot));
const everySlotIsKnownBusinessRule = (slots: string[]): boolean =>
	slots.every((slot) => VALIDATION_GRAPHQL_CODES.has(slot));

const RTK_STATUS_CLASSIFICATION: Record<string, RtkErrorClassification> = {
	CUSTOM_ERROR: "unknown",
	FETCH_ERROR: "network",
	PARSING_ERROR: "client",
	TIMEOUT_ERROR: "timeout"
};

/** Map an RTK baseQuery error to a high-level classification that Sentry can
 * group on. Keeps the logic in one place so tags are consistent across APIs. */
function classifyRtkError(error: unknown): RtkErrorClassification {
	const err = toRtkErrorShape(error);
	if (!err) return "unknown";

	const status = extractStatusCode(err);

	if (status === 401 || status === 403) return "auth";
	if (status !== undefined && status >= 500) return "server";
	if (status !== undefined && status >= 400) return "client";

	const payloadCodeSlots = toPayloadCodeSlots(error);
	if (payloadCodeSlots.length && everySlotIsKnownPayloadRefusal(payloadCodeSlots)) return "client";

	const codeSlots = toGraphqlCodeSlots(err);
	if (codeSlots.length) {
		if (hasAuthCode(codeSlots)) return "auth";
		if (hasServerCode(codeSlots)) return "server";
		if (everySlotIsKnownBusinessRule(codeSlots)) return "client";
	}

	const rtkStatus = typeof err.status === "string" ? err.status : undefined;
	if (rtkStatus && rtkStatus in RTK_STATUS_CLASSIFICATION) {
		return RTK_STATUS_CLASSIFICATION[rtkStatus];
	}

	const message = (extractTopLevelGraphqlMessage(err) ?? extractErrorMessage(err)).toLowerCase();
	if (!message) return status !== undefined ? "unknown" : "network";

	if (message.includes("abort") || message.includes("timeout") || message.includes("timed out")) {
		return "timeout";
	}
	if (
		message.includes("network request failed") ||
		message.includes("failed to fetch") ||
		message.includes("fetch is not defined") ||
		message.includes("networkerror")
	) {
		return "network";
	}
	if (message.includes("not authorized") || message.includes("unauthorized") || message.includes("unauthenticated")) {
		return "auth";
	}

	return "unknown";
}

const IDENTITY_MAX_LEN = 120;
const DASHED_UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;
const DASHLESS_UUID = /\b[0-9a-f]{32}\b/;
const OBJECT_ID = /\b[0-9a-f]{24}\b/;
const DIGIT_RUN = /\d+/;
const VOLATILE_ID_PATTERN = new RegExp(
	[DASHED_UUID, DASHLESS_UUID, OBJECT_ID, DIGIT_RUN].map((pattern) => pattern.source).join("|"),
	"gi"
);

export const scrubVolatileIds = (message: string): string => message.replace(VOLATILE_ID_PATTERN, "#");

export function describeErrorIdentity(error: unknown): string {
	const graphqlMessage = extractTopLevelGraphqlMessage(error);
	if (graphqlMessage) {
		return scrubVolatileIds(graphqlMessage).slice(0, IDENTITY_MAX_LEN);
	}
	if (error instanceof Error && error.name) return error.name;

	const err = toRtkErrorShape(error);
	if (!err) return typeof error;
	if (typeof err.name === "string" && err.name) return err.name;

	const constructorName = (error as object).constructor?.name;
	if (constructorName && constructorName !== "Object") return constructorName;
	const eventType = (err as { type?: unknown }).type;
	if (typeof eventType === "string" && eventType) return `event:${eventType}`;
	if (typeof err.status === "string" && err.status) return err.status;
	return "object";
}

export const GRAPHQL_ERROR_BREADCRUMB_CATEGORY = "graphql.error";

const NO_CODE = "no-code";

export interface GraphqlErrorBreadcrumbInput {
	endpoint: string;
	error: unknown;
	operationType: "query" | "mutation";
}

export interface GraphqlErrorBreadcrumb {
	category: string;
	data: { endpoint: string; graphqlCode: string; operationType: string; statusCode: null | number };
	level: "warning";
	message: string;
}

export const buildGraphqlErrorBreadcrumb = ({
	endpoint,
	error,
	operationType
}: GraphqlErrorBreadcrumbInput): GraphqlErrorBreadcrumb => {
	const graphqlCode = toGraphqlCodeSlots(error).find((slot) => slot !== NO_CODE_SLOT) ?? NO_CODE;
	const statusCode = extractStatusCode(error) ?? null;

	return {
		category: GRAPHQL_ERROR_BREADCRUMB_CATEGORY,
		data: { endpoint, graphqlCode, operationType, statusCode },
		level: "warning",
		message: `${endpoint}: ${graphqlCode}`
	};
};

export default classifyRtkError;
